import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronRight,
  Hash,
  Sparkles,
  ArrowRight,
  Star,
  Mail,
  Heart,
  TrendingUp,
  Shield,
  Target,
  Lightbulb,
  Users,
  Calendar
} from 'lucide-react';

/* ─── colour tokens ─── */
const C = {
  indigo: '#6366F1',
  indigoLight: '#818CF8',
  violet: '#8B5CF6',
  violetLight: '#A78BFA',
  gold: '#C89B3C',
  goldLight: '#E8C872',
  cyan: '#22D3EE',
  emerald: '#34D399',
  rose: '#FB7185',
  cream: '#F4EFE6',
  charcoal: '#2B2B2B',
  dark: '#0F0F14',
  darkCard: '#16161D',
  card: '#1C1C26',
  cardBorder: '#2A2A3A',
};

/* ─── Countdown Timer ─── */
const CountdownTimer = () => {
  const launchDate = new Date(Date.now() + 14 * 86400000);
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = launchDate - new Date();
      if (diff <= 0) return;
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex gap-4 md:gap-6 justify-center" data-testid="countdown-timer">
      {Object.entries(time).map(([label, val]) => (
        <div key={label} className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl md:text-4xl font-bold"
            style={{ background: `${C.indigo}15`, color: C.indigoLight, border: `1px solid ${C.indigo}30` }}>
            {String(val).padStart(2, '0')}
          </div>
          <p className="text-xs mt-2 uppercase tracking-widest" style={{ color: '#888' }}>{label}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Feature Card ─── */
const FeatureCard = ({ icon: Icon, title, desc, accent }) => (
  <Card className="border-0 group cursor-pointer hover:-translate-y-1 transition-all duration-500"
    style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}
    data-testid={`feature-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${accent}15` }}>
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: '#999' }}>{desc}</p>
    </CardContent>
  </Card>
);

/* ─── Core Number Card ─── */
const CoreNumberCard = ({ number, name, meaning, color }) => (
  <div className="rounded-xl p-5 group hover:-translate-y-1 transition-all duration-500"
    style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
    <div className="flex items-center gap-4 mb-3">
      <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold shrink-0"
        style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
        {number}
      </div>
      <div>
        <p className="text-white font-semibold">{name}</p>
        <p className="text-xs" style={{ color: '#888' }}>{meaning}</p>
      </div>
    </div>
  </div>
);

/* ─── Trait Tag ─── */
const TraitTag = ({ label, color }) => (
  <span className="inline-block px-3 py-1.5 rounded-md text-xs font-medium border"
    style={{ color, borderColor: `${color}40`, background: `${color}10` }}>
    {label}
  </span>
);

/* ─── Main Page ─── */
const LifeCodeComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'lifecode' })
      });
      const data = await res.json();
      setSubmitMsg(data.message || "You're on the list!");
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitMsg("Something went wrong. Please try again.");
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'core', label: 'Core Numbers' },
    { id: 'year', label: 'Personal Year' },
    { id: 'compatibility', label: 'Compatibility' },
  ];

  return (
    <div className="min-h-screen" style={{ background: C.dark, color: C.cream }}>

      {/* ─── BG Pattern ─── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-[0.04]"
          style={{ background: `radial-gradient(circle, ${C.indigo}, transparent 70%)` }} />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.03]"
          style={{ background: `radial-gradient(circle, ${C.violet}, transparent 70%)` }} />
        {/* Number grid */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02]">
          <div className="text-[200px] font-bold text-white tracking-[0.3em] select-none">
            1 2 3
          </div>
        </div>
      </div>

      {/* ─── NAV ─── */}
      <nav className="relative z-10 border-b" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hash className="w-6 h-6" style={{ color: C.indigoLight }} />
            <span className="font-semibold text-white text-lg tracking-wide">Life<span style={{ color: C.indigoLight }}>Code</span><span className="text-xs ml-1" style={{ color: '#666' }}>.live</span></span>
          </div>
          <a href="#waitlist">
            <Button className="text-sm font-medium px-5"
              style={{ background: C.indigo, color: '#fff' }}>
              Join Waitlist
            </Button>
          </a>
        </div>
      </nav>

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative z-10 pt-20 pb-28 md:pt-28 md:pb-36" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8"
            style={{ background: `${C.indigo}12`, border: `1px solid ${C.indigo}30` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: C.indigoLight }} />
            <span className="text-xs tracking-widest uppercase" style={{ color: C.indigoLight }}>Coming Soon</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
            Every Number Has a Meaning.<br className="hidden sm:block" />
            <span className="font-serif italic" style={{ color: C.violetLight }}>
              Decode Yours.
            </span>
          </h1>

          <p className="text-base md:text-lg max-w-2xl mx-auto mb-4 leading-relaxed" style={{ color: '#999' }}>
            Discover your Life Path, Expression, Soul Urge, and Personal Year numbers.
            Understand your character, strengths, challenges, and the cycles shaping your destiny.
          </p>

          <p className="text-sm max-w-xl mx-auto mb-10" style={{ color: '#777' }}>
            A modern numerology platform turning ancient number wisdom into clear, actionable insights. Launching soon.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium"
                style={{ background: C.indigo, color: '#fff' }}>
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium border"
                style={{ background: 'transparent', color: C.cream, borderColor: C.cardBorder }}>
                <Mail className="w-4 h-4 mr-2" />
                Notify Me When Live
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════ DASHBOARD PREVIEW ═══════════════ */}
      <section className="relative z-10 pb-24" data-testid="dashboard-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.indigoLight }}>Platform Preview</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              What the <span className="font-serif italic" style={{ color: C.violetLight }}>Dashboard</span> Looks Like
            </h2>
          </div>

          <div className="rounded-2xl p-1" style={{ background: `linear-gradient(135deg, ${C.indigo}25, ${C.cardBorder}, ${C.violet}20)` }}>
            <div className="rounded-xl overflow-hidden" style={{ background: C.dark }}>

              {/* Dashboard Header */}
              <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: C.cardBorder }}>
                <div>
                  <p className="text-white font-semibold text-lg">Numerology Life Code Report</p>
                  <p className="text-xs" style={{ color: '#666' }}>Birth Date: March 15, 1988</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ background: `${C.indigo}20` }}>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.indigoLight }} />
                  <span className="text-xs" style={{ color: C.indigoLight }}>Full Analysis</span>
                </div>
              </div>

              {/* Tab Bar */}
              <div className="flex border-b" style={{ borderColor: C.cardBorder }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex-1 py-3.5 px-4 text-sm font-medium transition-all relative"
                    style={{
                      color: activeTab === tab.id ? '#fff' : '#666',
                      background: activeTab === tab.id ? C.card : 'transparent',
                    }}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: C.indigo }} />
                    )}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-6 md:p-8">

                {/* ─── PROFILE TAB ─── */}
                {activeTab === 'profile' && (
                  <div data-testid="tab-content-profile">
                    {/* Life Path Number Hero */}
                    <div className="rounded-xl p-6 mb-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: C.indigoLight }}>Life Path Number</p>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold"
                              style={{ background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`, color: '#fff' }}>
                              7
                            </div>
                            <div>
                              <p className="text-2xl text-white font-light">The Seeker</p>
                              <p className="text-sm" style={{ color: '#888' }}>Analytical, Spiritual, Introspective</p>
                            </div>
                          </div>
                          <p className="text-sm leading-relaxed mb-5" style={{ color: '#999' }}>
                            Life Path 7 is the path of the thinker and truth-seeker. You are naturally drawn to understanding
                            the deeper meaning of life. Your analytical mind combined with spiritual intuition makes you
                            exceptionally insightful. You thrive in environments where you can research, reflect, and discover.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {['analytical', 'intuitive', 'spiritual', 'philosophical', 'independent'].map(tag => (
                              <TraitTag key={tag} label={tag} color={C.indigoLight} />
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>Vibration</p>
                          <div className="w-24 h-24 rounded-full flex items-center justify-center"
                            style={{ background: `${C.indigo}15`, border: `2px solid ${C.indigo}50` }}>
                            <span className="text-3xl font-bold" style={{ color: C.indigoLight }}>7</span>
                          </div>
                          <p className="text-xs mt-2" style={{ color: C.indigoLight }}>Seeker Energy</p>
                        </div>
                      </div>
                    </div>

                    {/* Personality Snapshot */}
                    <div className="rounded-xl p-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <h3 className="text-lg font-serif italic mb-6" style={{ color: C.violetLight }}>Personality Snapshot</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Strengths</p>
                          <div className="flex flex-wrap gap-2">
                            {['deep thinker', 'perceptive', 'wise', 'focused', 'truth-seeking'].map(t => (
                              <TraitTag key={t} label={t} color={C.emerald} />
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Challenges</p>
                          <div className="flex flex-wrap gap-2">
                            {['overthinking', 'isolation', 'skepticism', 'perfectionism'].map(t => (
                              <TraitTag key={t} label={t} color={C.rose} />
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Best Careers</p>
                          <div className="flex flex-wrap gap-2">
                            {['researcher', 'analyst', 'writer', 'strategist', 'philosopher'].map(t => (
                              <TraitTag key={t} label={t} color={C.cyan} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── CORE NUMBERS TAB ─── */}
                {activeTab === 'core' && (
                  <div data-testid="tab-content-core">
                    <h3 className="text-lg font-serif italic mb-6" style={{ color: C.violetLight }}>Your Core Numbers</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <CoreNumberCard number={7} name="Life Path Number" meaning="Your life purpose and journey — The Seeker" color={C.indigo} />
                      <CoreNumberCard number={3} name="Expression Number" meaning="Your natural talents and abilities — The Communicator" color={C.violet} />
                      <CoreNumberCard number={9} name="Soul Urge Number" meaning="Your heart's deepest desire — The Humanitarian" color={C.rose} />
                      <CoreNumberCard number={5} name="Personality Number" meaning="How others perceive you — The Adventurer" color={C.cyan} />
                      <CoreNumberCard number={1} name="Birthday Number" meaning="Your special gift to the world — The Leader" color={C.emerald} />
                      <CoreNumberCard number={22} name="Maturity Number" meaning="Your true self emerging over time — Master Builder" color={C.gold} />
                    </div>

                    {/* Number Grid */}
                    <div className="rounded-xl p-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Number Frequency in Your Chart</p>
                      <div className="grid grid-cols-9 gap-2">
                        {[1,2,3,4,5,6,7,8,9].map(num => {
                          const freq = [3,1,2,0,2,1,3,1,2][num-1];
                          const intensity = freq === 0 ? 0.1 : freq === 1 ? 0.3 : freq === 2 ? 0.6 : 1;
                          return (
                            <div key={num} className="text-center">
                              <div className="aspect-square rounded-lg flex items-center justify-center text-lg font-bold mb-1"
                                style={{
                                  background: `${C.indigo}${Math.round(intensity * 40).toString(16).padStart(2,'0')}`,
                                  color: intensity > 0.5 ? '#fff' : '#666',
                                  border: `1px solid ${C.indigo}${Math.round(intensity * 60).toString(16).padStart(2,'0')}`
                                }}>
                                {num}
                              </div>
                              <p className="text-[10px]" style={{ color: '#888' }}>x{freq}</p>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs mt-4 text-center" style={{ color: '#666' }}>
                        Missing: <span style={{ color: C.rose }}>4</span> — indicates a need to build stronger foundations and discipline
                      </p>
                    </div>
                  </div>
                )}

                {/* ─── PERSONAL YEAR TAB ─── */}
                {activeTab === 'year' && (
                  <div data-testid="tab-content-year">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-serif italic" style={{ color: C.violetLight }}>Personal Year Cycle</h3>
                      <span className="px-3 py-1 rounded text-xs font-medium uppercase tracking-wider"
                        style={{ border: `1px solid ${C.indigo}40`, color: C.indigoLight }}>2026</span>
                    </div>

                    {/* Current Year */}
                    <div className="rounded-xl p-6 mb-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold"
                          style={{ background: `linear-gradient(135deg, ${C.emerald}, ${C.cyan})`, color: '#fff' }}>
                          1
                        </div>
                        <div>
                          <p className="text-white font-semibold text-xl">Personal Year 1 — New Beginnings</p>
                          <p className="text-sm" style={{ color: '#888' }}>A year of fresh starts, independence, and planting seeds</p>
                        </div>
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: '#999' }}>
                        This is the start of a new 9-year cycle. It's a powerful time to launch new projects, take initiative,
                        and assert your independence. Decisions made this year will set the tone for the next decade.
                        Focus on <span className="text-white">self-leadership</span> and <span className="text-white">bold action</span>.
                      </p>
                    </div>

                    {/* 9-Year Cycle */}
                    <div className="rounded-xl p-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <p className="text-xs uppercase tracking-widest mb-5" style={{ color: '#777' }}>Your 9-Year Cycle Map</p>
                      <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
                        {[
                          { yr: '2026', num: 1, label: 'Begin', active: true },
                          { yr: '2027', num: 2, label: 'Partner' },
                          { yr: '2028', num: 3, label: 'Create' },
                          { yr: '2029', num: 4, label: 'Build' },
                          { yr: '2030', num: 5, label: 'Change' },
                          { yr: '2031', num: 6, label: 'Nurture' },
                          { yr: '2032', num: 7, label: 'Reflect' },
                          { yr: '2033', num: 8, label: 'Harvest' },
                          { yr: '2034', num: 9, label: 'Complete' },
                        ].map(y => (
                          <div key={y.yr} className="text-center rounded-lg p-2"
                            style={{
                              background: y.active ? `${C.indigo}20` : 'transparent',
                              border: y.active ? `1px solid ${C.indigo}50` : `1px solid ${C.cardBorder}`,
                            }}>
                            <p className="text-[10px] mb-1" style={{ color: y.active ? C.indigoLight : '#666' }}>{y.yr}</p>
                            <p className="text-xl font-bold" style={{ color: y.active ? '#fff' : '#555' }}>{y.num}</p>
                            <p className="text-[10px]" style={{ color: y.active ? C.indigoLight : '#777' }}>{y.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── COMPATIBILITY TAB ─── */}
                {activeTab === 'compatibility' && (
                  <div data-testid="tab-content-compatibility">
                    <h3 className="text-lg font-serif italic mb-6" style={{ color: C.violetLight }}>Number Compatibility</h3>

                    <div className="rounded-xl p-6 mb-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <p className="text-xs uppercase tracking-widest mb-5" style={{ color: '#777' }}>Your Compatibility Chart (Life Path 7)</p>
                      <div className="space-y-4">
                        {[
                          { num: 5, label: 'The Adventurer', pct: 92, note: 'Excellent — intellectual stimulation and freedom' },
                          { num: 3, label: 'The Communicator', pct: 85, note: 'Strong — creativity meets depth' },
                          { num: 9, label: 'The Humanitarian', pct: 80, note: 'Good — shared spiritual values' },
                          { num: 1, label: 'The Leader', pct: 72, note: 'Moderate — independence can clash' },
                          { num: 4, label: 'The Builder', pct: 65, note: 'Moderate — stability vs. freedom tension' },
                          { num: 8, label: 'The Powerhouse', pct: 55, note: 'Challenging — different priorities' },
                        ].map(c => (
                          <div key={c.num}>
                            <div className="flex justify-between items-center mb-1.5">
                              <div className="flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                                  style={{ background: `${C.indigo}20`, color: C.indigoLight }}>{c.num}</span>
                                <span className="text-white text-sm font-medium">{c.label}</span>
                              </div>
                              <span className="text-sm font-semibold" style={{ color: c.pct > 80 ? C.emerald : c.pct > 65 ? C.gold : C.rose }}>{c.pct}%</span>
                            </div>
                            <div className="h-1.5 rounded-full mb-1" style={{ background: '#333' }}>
                              <div className="h-full rounded-full" style={{
                                width: `${c.pct}%`,
                                background: c.pct > 80 ? C.emerald : c.pct > 65 ? C.gold : C.rose
                              }} />
                            </div>
                            <p className="text-xs" style={{ color: '#777' }}>{c.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.indigoLight }}>Platform Features</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              Powerful <span className="font-serif italic" style={{ color: C.violetLight }}>Numerology</span> Intelligence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={Hash} title="Core Numbers Engine"
              desc="Calculate your Life Path, Expression, Soul Urge, Personality, Birthday, and Maturity numbers instantly from your birth data."
              accent={C.indigo} />
            <FeatureCard icon={Calendar} title="Personal Year Forecast"
              desc="Understand your current position in the 9-year cycle. Know when to start, build, harvest, or rest."
              accent={C.emerald} />
            <FeatureCard icon={Users} title="Compatibility Analysis"
              desc="Compare Life Path numbers to understand relationship dynamics in love, business, and friendship."
              accent={C.rose} />
            <FeatureCard icon={Target} title="Career Alignment"
              desc="Discover which career paths align with your numerological profile for maximum fulfilment and success."
              accent={C.cyan} />
            <FeatureCard icon={TrendingUp} title="Pinnacle & Challenge Cycles"
              desc="Map the four major pinnacle periods of your life and the challenges that shape your growth."
              accent={C.violet} />
            <FeatureCard icon={Lightbulb} title="Name Analysis"
              desc="Analyse the numerical vibration of your name to understand how it influences your expression and destiny."
              accent={C.goldLight} />
          </div>
        </div>
      </section>

      {/* ═══════════════ PHILOSOPHY ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.violet }}>Philosophy</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
            Ancient Numbers, <span className="font-serif italic" style={{ color: C.violetLight }}>Modern Insight</span>
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#aaa' }}>
            <span className="text-white font-medium">Numerology</span> is a divination system that analyses the mystical relationship
            between numbers, names, and birth dates to reveal a person's character, strengths, challenges, and life purpose.
          </p>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#aaa' }}>
            Numbers carry unique <span className="text-white font-medium">vibrational energies</span>. These energies influence personality,
            behaviour, opportunities, and the path you are likely to follow in life.
          </p>
          <p className="text-base leading-relaxed" style={{ color: '#aaa' }}>
            Our platform transforms this wisdom into a <span className="text-white font-medium">modern analytical dashboard</span>,
            making numerology insights clear, structured, and actionable.
          </p>

          {/* Numbers Display */}
          <div className="flex justify-center gap-4 mt-12">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <div key={n} className="text-center group cursor-default">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-2 transition-transform group-hover:scale-110"
                  style={{ background: `${C.indigo}15`, color: C.indigoLight, border: `1px solid ${C.indigo}30` }}>
                  {n}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ COUNTDOWN + WAITLIST ═══════════════ */}
      <section className="relative z-10 py-28" id="waitlist" data-testid="waitlist-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.indigoLight }}>Launching Soon</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-10">
            The Future of <span className="font-serif italic" style={{ color: C.violetLight }}>Numerology</span>
          </h2>

          <CountdownTimer />

          <p className="mt-10 mb-8 text-base" style={{ color: '#999' }}>
            We are building something powerful for the Numerology community.<br />
            Join the waitlist to get early access.
          </p>

          {submitted ? (
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl"
              style={{ background: `${C.emerald}15`, border: `1px solid ${C.emerald}30`, color: C.emerald }}>
              <Star className="w-5 h-5" />
              <span className="font-medium">{submitMsg}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="waitlist-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 h-12 text-white border-0"
                style={{ background: C.card, borderColor: C.cardBorder }}
                data-testid="waitlist-email"
              />
              <Button type="submit" disabled={submitting} className="h-12 px-8 font-medium"
                style={{ background: C.indigo, color: '#fff' }}
                data-testid="waitlist-submit">
                {submitting ? 'Joining...' : 'Join Waitlist'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="relative z-10 border-t py-12" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
                <Hash className="w-5 h-5" style={{ color: C.indigoLight }} />
                <span className="text-white font-semibold">LifeCode.live</span>
              </div>
              <p className="text-xs" style={{ color: '#666' }}>Powered by Life Decoder</p>
            </div>
            <div className="flex items-center gap-6">
              {['About', 'Contact', 'Privacy Policy'].map((link) => (
                <a key={link} href="#" className="text-sm transition-colors hover:text-white" style={{ color: '#777' }}>{link}</a>
              ))}
            </div>
          </div>

          <div className="mt-8 pt-6 flex flex-wrap justify-center gap-4" style={{ borderTop: '1px solid #ffffff08' }}>
            {[
              { name: 'Stevenyong.live', url: 'https://stevenyong.live' },
              { name: 'Bazi.live', url: 'https://bazi.live' },
              { name: 'QMDJ.live', url: 'https://qmdj.live' },
              { name: 'Fengshuiplotter.live', url: 'https://fengshuiplotter.live' },
              { name: 'Lifecode.live', url: 'https://lifecode.live' },
              { name: 'Lifetunes.live', url: 'https://lifetunes.live' },
              { name: 'Onlinetongshu.com', url: 'https://onlinetongshu.com' },
            ].map((link) => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className="text-xs transition-colors hover:text-white" style={{ color: C.indigoLight }}>
                {link.name}
              </a>
            ))}
          </div>
          <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid #ffffff08' }}>
            <p className="text-xs" style={{ color: '#555' }}>Life Decoder &copy; {new Date().getFullYear()} LifeCode.live - All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LifeCodeComingSoon;
